import React, { useState, useContext, useRef, forwardRef, useImperativeHandle } from 'react';
import { Paper, Box, Typography } from '@mui/material';
import AuthContext from '../../../../contexts/AuthContext';
import { useClinicalSession } from '../../../../contexts/ClinicalSessionContext';
import useAllStudentGoalsData from '../../../../hooks/useAllStudentGoalsData';
import { useGoalsData } from './hooks/useGoalsData';
import GoalSearchSection from './components/GoalSearchSection';
import GoalTabsSection from './components/GoalTabsSection';

const GoalsDataCard = forwardRef(({
  sessionId,
  user,
  userName,
  onFieldChange,
  dataEntryRef,
  draftEntries = {},
  onUpsertDraft,
  onRemoveDraft,
  formState,
  originalData,
}, ref) => {
  const { user: currentUser, typeOfUser } = useContext(AuthContext);
  const { getSession } = useClinicalSession();
  const session = getSession(sessionId);
  const sessionData = session?.sessionData || {};

  const originalGoalIds = originalData?.Goals?.map((goal) => goal.goalID);
  const originalGoalData = originalData?.Goals;

  const [goalsSearchTerm, setGoalsSearchTerm] = useState('');
  const [goalsExpanded, setGoalsExpanded] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const goalDataRef = useRef({});
  
  // Create ref for GoalTabsSection
  const goalTabsSectionRef = useRef(null);

  // Expose saveAll method via ref
  useImperativeHandle(ref, () => ({
    saveAll: async () => {
      if (goalTabsSectionRef.current && typeof goalTabsSectionRef.current.saveAllGoals === 'function') {
        return await goalTabsSectionRef.current.saveAllGoals();
      }
      return [];
    }
  }), []);

  // Fetch goals data
  const { data: studentGoalsData = [], isLoading: isLoadingStudentGoalsData } =
    useAllStudentGoalsData(currentUser?.uid, typeOfUser);

  // Process goals using custom hook
  const { displayedGoals, filteredGoals, selectedGoals } = useGoalsData(
    studentGoalsData,
    user,
    currentUser,
    formState,
    goalsSearchTerm,
    goalsExpanded,
    sessionData
  );

  const handleGoalToggle = (goal) => {
    const isAlreadySelected = selectedGoals.some((g) => g.id === goal.id);
    const newSelected = isAlreadySelected
      ? selectedGoals.filter((g) => g.id !== goal.id)
      : [...selectedGoals, goal];

    // Update parent component with IDs
    if (onFieldChange) {
      onFieldChange(
        'selectedGoals',
        newSelected.map((g) => g.id)
      );
    }

    if (isAlreadySelected && typeof onRemoveDraft === 'function') {
      onRemoveDraft(goal.id);
    }
  };

  const handleRemoveGoal = (goalId) => {
    const newSelected = selectedGoals.filter((g) => g.id !== goalId);

    // Update parent component
    if (onFieldChange) {
      onFieldChange(
        'selectedGoals',
        newSelected.map((g) => g.id)
      );
    }

    if (typeof onRemoveDraft === 'function') {
      onRemoveDraft(goalId);
    }

    // Reset tab if we removed the current tab
    if (selectedTabIndex >= newSelected.length && selectedTabIndex > 0) {
      setSelectedTabIndex(selectedTabIndex - 1);
    }
  };

  const registerModalData = (entryKey, getData) => {
    goalDataRef.current[entryKey] = getData;
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTabIndex(newValue);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: '#f8f9ff',
          borderBottom: '1px solid #e8e9ff',
          px: 3,
          py: 2,
        }}
      >
        <Typography
          variant='h6'
          sx={{
            fontFamily: 'Lato, sans-serif',
            fontWeight: 600,
            color: '#9291ef',
          }}
        >
          Goals ({selectedGoals.length})
        </Typography>
      </Box>

      {/* Search and Goal Selection Section */}
      <GoalSearchSection
        goalsSearchTerm={goalsSearchTerm}
        onSearchChange={setGoalsSearchTerm}
        displayedGoals={displayedGoals}
        filteredGoals={filteredGoals}
        goalsExpanded={goalsExpanded}
        onToggleExpanded={() => setGoalsExpanded(!goalsExpanded)}
        selectedGoals={selectedGoals}
        onGoalToggle={handleGoalToggle}
        originalGoalIds={originalGoalIds}
        isLoadingStudentGoalsData={isLoadingStudentGoalsData}
      />

      {/* Selected Goals with Tabbed Data Entry */}
      <GoalTabsSection
        ref={goalTabsSectionRef}
        selectedGoals={selectedGoals}
        selectedTabIndex={selectedTabIndex}
        onTabChange={handleTabChange}
        user={user}
        userName={userName}
        formState={formState}
        studentGoalsData={studentGoalsData}
        originalGoalIds={originalGoalIds}
        originalGoalData={originalGoalData}
        registerModalData={registerModalData}
        dataEntryRef={dataEntryRef}
        draftEntries={draftEntries}
        onUpsertDraft={onUpsertDraft}
        onRemoveDraft={onRemoveDraft}
      />
    </Paper>
  );
});

export default GoalsDataCard;
